import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ShoppingCart, Globe, Radio, RefreshCw, Headphones, Users, ArrowLeft } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAppContext, Package } from '../context/AppContext';
import { Toaster, toast } from 'sonner';
const categoryConfig = {
  hosting: {
    title: 'Web Hosting',
    icon: Globe,
    description: 'Fast, secure, and reliable shared hosting for your personal or small business websites.',
    color: 'fox',
    bgClass: 'bg-fox-500/10',
    textClass: 'text-fox-500'
  },
  radio: {
    title: 'Radio Station Rentals',
    icon: Radio,
    description: 'Launch your own internet radio station with our fully managed streaming servers. Auto DJ, live broadcasting, and listener analytics included.',
    color: 'amber',
    bgClass: 'bg-amber-500/10',
    textClass: 'text-amber-500'
  },
  reseller: {
    title: 'Reseller Accounts',
    icon: RefreshCw,
    description: 'Start your own hosting business with our white-label reseller plans. Full WHM access, custom branding, and WHMCS billing integration.',
    color: 'fox',
    bgClass: 'bg-fox-500/10',
    textClass: 'text-fox-500'
  },
  shoutcast: {
    title: 'Shoutcast Packages',
    icon: Headphones,
    description: 'Dedicated Shoutcast v2 DNAS servers with Centova Cast control panel. Stream your audio content to the world with professional-grade infrastructure.',
    color: 'purple',
    bgClass: 'bg-purple-500/10',
    textClass: 'text-purple-500'
  },
  'shoutcast-reseller': {
    title: 'Shoutcast Reseller',
    icon: Users,
    description: 'Resell Shoutcast hosting under your own brand. Full Centova Cast reseller panel, white-label branding, and client management tools included.',
    color: 'cyan',
    bgClass: 'bg-cyan-500/10',
    textClass: 'text-cyan-500'
  }
};
export function Services() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const [isYearly, setIsYearly] = useState(false);
  const {
    packages,
    addToCart
  } = useAppContext();
  const config = categoryConfig[category as keyof typeof categoryConfig];
  const Icon = config?.icon || Globe;
  const plans = packages.filter(p => p.category === category && p.active);
  const handleAddToCart = (plan: Package) => {
    addToCart({
      packageId: plan.id,
      name: plan.name,
      category: plan.category,
      price: isYearly ? plan.yearlyPrice : plan.monthlyPrice,
      billing: isYearly ? 'yearly' : 'monthly',
      quantity: 1
    });
    toast.success(`${plan.name} added to cart!`);
  };
  if (!config) {
    return <div className="min-h-screen bg-slate-950 flex flex-col" data-id="element-2649">
        <Navbar data-id="element-2650" />
        <main className="flex-1 flex items-center justify-center pt-20" data-id="element-2651">
          <div className="text-center" data-id="element-2652">
            <h1 className="text-3xl font-bold text-white mb-4" data-id="element-2653">
              Category Not Found
            </h1>
            <Link to="/" className="text-fox-500 hover:text-fox-400 flex items-center gap-2 justify-center" data-id="element-2654">
              <ArrowLeft size={16} data-id="element-2655" /> Back to Home
            </Link>
          </div>
        </main>
        <Footer data-id="element-2656" />
      </div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950 flex flex-col" data-id="element-2657">
      <Navbar data-id="element-2658" />
      <Toaster theme="dark" position="top-right" data-id="element-2659" />

      <main className="flex-1 pt-32 pb-24" data-id="element-2660">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2661">
          {/* Hero Section */}
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2662">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className={`w-20 h-20 ${config.bgClass} rounded-full flex items-center justify-center mx-auto mb-6`} data-id="element-2663">
              <Icon className={`${config.textClass} w-10 h-10`} data-id="element-2664" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6" data-id="element-2665">
              {config.title}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-lg text-slate-400 max-w-2xl mx-auto mb-12" data-id="element-2666">
              {config.description}
            </motion.p>

            {/* Toggle */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="flex items-center justify-center gap-4" data-id="element-2667">
              <span className={`text-sm font-medium ${!isYearly ? 'text-white' : 'text-slate-400'}`} data-id="element-2668">
                Monthly
              </span>
              <button onClick={() => setIsYearly(!isYearly)} className="w-14 h-7 bg-slate-800 rounded-full p-1 relative transition-colors hover:bg-slate-700 focus:outline-none" data-id="element-2669">
                <motion.div className="w-5 h-5 bg-fox-500 rounded-full" animate={{
                x: isYearly ? 28 : 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-2670" />
              </button>
              <span className={`text-sm font-medium flex items-center gap-2 ${isYearly ? 'text-white' : 'text-slate-400'}`} data-id="element-2671">
                Yearly{' '}
                <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full" data-id="element-2672">
                  Save 20%
                </span>
              </span>
            </motion.div>
          </div>

          {/* Plans Grid */}
          {plans.length === 0 ? <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl max-w-3xl mx-auto" data-id="element-2673">
              <h2 className="text-2xl font-bold text-white mb-2" data-id="element-2674">
                No plans available
              </h2>
              <p className="text-slate-400" data-id="element-2675">
                Check back later for new offerings in this category.
              </p>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-2676">
              {plans.map((plan, index) => <motion.div key={plan.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`relative rounded-3xl p-8 flex flex-col ${plan.highlighted ? 'bg-slate-800 border-2 border-fox-500 shadow-[0_0_30px_rgba(234,88,12,0.15)] transform md:-translate-y-4' : 'bg-slate-900 border border-slate-800'}`} data-id="element-2677">
                  {plan.highlighted && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-fox-600 to-amber-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg" data-id="element-2678">
                      Most Popular
                    </div>}

                  <div className="mb-6 flex-1" data-id="element-2679">
                    <h4 className="text-xl font-heading font-bold text-white mb-2" data-id="element-2680">
                      {plan.name}
                    </h4>
                    <p className="text-slate-400 text-sm h-10" data-id="element-2681">
                      {plan.description}
                    </p>
                  </div>

                  <div className="mb-8" data-id="element-2682">
                    <span className="text-4xl font-heading font-bold text-white" data-id="element-2683">
                      ${isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-slate-400" data-id="element-2684">
                      /{isYearly ? 'yr' : 'mo'}
                    </span>
                  </div>

                  <ul className="space-y-4 mb-8 flex-1" data-id="element-2685">
                    {plan.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-2686">
                        <div className="mt-1 bg-fox-500/20 p-0.5 rounded-full shrink-0" data-id="element-2687">
                          <Check size={14} className="text-fox-500" data-id="element-2688" />
                        </div>
                        <span className="text-slate-300 text-sm" data-id="element-2689">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <button onClick={() => handleAddToCart(plan)} className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${plan.highlighted ? 'bg-fox-600 hover:bg-fox-500 text-white shadow-lg hover:shadow-fox-500/25' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`} data-id="element-2690">
                    <ShoppingCart size={16} data-id="element-2691" />
                    Add to Cart
                  </button>
                </motion.div>)}
            </div>}
        </div>
      </main>
      <Footer data-id="element-2692" />
    </motion.div>;
}